import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Mail, Minus, PackageCheck, Plus, ShoppingBag, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { getProduct, inr, imgSrc } from "@/api";
import { useCart } from "@/context/CartContext";
import { FadeIn, LotusMark } from "@/components/Reveal";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const ProductDetail = () => {
  const { id } = useParams();
  const { add } = useCart();
  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [imageIdx, setImageIdx] = useState(0);
  const [size, setSize] = useState(null);
  const [qty, setQty] = useState(1);
  const [touchStartX, setTouchStartX] = useState(null);

  useEffect(() => {
    setProduct(null);
    setNotFound(false);
    setImageIdx(0);
    setSize(null);
    setQty(1);
    getProduct(id).then(setProduct).catch(() => setNotFound(true));
  }, [id]);

  const selectedStock = size ? (product?.stock?.[size] ?? 0) : 0;

  const handleAdd = () => {
    if (!size) {
      toast.error("Choose your size first", { description: "Select a size to add this piece to your wardrobe." });
      return;
    }
    if (qty > selectedStock) {
      toast.error("Not enough stock", { description: `Only ${selectedStock} available in size ${size}.` });
      setQty(Math.max(1, selectedStock));
      return;
    }
    add(product, size, qty);
    toast.success("Added to your wardrobe.", { description: `${product.name} · Size ${size} · Qty ${qty}` });
  };

  if (notFound) {
    return (
      <div data-testid="product-not-found" className="pt-40 pb-32 text-center px-6">
        <p className="font-display italic text-3xl text-maroon-deep/70">This piece has slipped off the rack.</p>
        <Link to="/shop" className="mt-6 inline-block text-xs tracking-[0.3em] uppercase text-gold-dark border-b border-gold pb-1">
          Back to the Collection
        </Link>
      </div>
    );
  }

  if (!product) {
    return (
      <div data-testid="product-loading" className="pt-40 pb-32 text-center">
        <p className="font-display italic text-2xl text-maroon-deep/60">Drawing the curtain…</p>
      </div>
    );
  }

  return (
    <div data-testid="product-detail-page" className="pt-20 pb-24 sm:pb-0">
      <div className="max-w-7xl mx-auto px-4 sm:px-12 lg:px-20 py-6 sm:py-10">
        <Link
          to="/shop"
          data-testid="product-back-link"
          className="inline-flex items-center gap-2 text-[11px] tracking-[0.3em] uppercase text-clay hover:text-gold-dark transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> The Collection
        </Link>

        <div className="mt-6 sm:mt-8 grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-20">
          <FadeIn>
            <div className="relative overflow-hidden ring-1 ring-gold/30 bg-ivory-deep aspect-[4/5] sm:aspect-[3/4]">
              {product.badge && (
                <span className="absolute top-5 left-5 z-10 bg-ivory-card/90 backdrop-blur px-3 py-1 text-[9px] tracking-[0.3em] uppercase text-gold-dark border border-gold/40">
                  {product.badge}
                </span>
              )}
              <motion.img
                key={imageIdx}
                initial={{ opacity: 0.3, scale: 1.02 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.7 }}
                src={imgSrc(product.images[imageIdx])}
                alt={product.name}
                data-testid="product-main-image"
                loading="eager"
                fetchPriority="high"
                decoding="async"
                className="w-full h-full object-cover select-none"
                draggable={false}
                onTouchStart={(e) => setTouchStartX(e.touches[0].clientX)}
                onTouchEnd={(e) => {
                  if (touchStartX === null || product.images.length < 2) return;
                  const delta = e.changedTouches[0].clientX - touchStartX;
                  if (Math.abs(delta) > 45) {
                    setImageIdx((current) =>
                      delta < 0
                        ? Math.min(product.images.length - 1, current + 1)
                        : Math.max(0, current - 1)
                    );
                  }
                  setTouchStartX(null);
                }}
              />
              {product.images.length > 1 && (
                <span className="absolute bottom-4 right-4 z-10 bg-maroon/80 text-ivory px-3 py-1.5 text-[9px] tracking-[0.22em] uppercase backdrop-blur-sm">
                  {imageIdx + 1} / {product.images.length}
                </span>
              )}
            </div>
            <div className="mt-3 flex gap-2.5 overflow-x-auto pb-1 scrollbar-hide">
              {product.images.map((img, i) => (
                <button
                  key={i}
                  data-testid={`product-thumbnail-${i}`}
                  onClick={() => setImageIdx(i)}
                  className={`w-16 h-20 sm:w-20 sm:h-24 overflow-hidden shrink-0 transition-all duration-300 ${
                    i === imageIdx ? "ring-2 ring-gold" : "ring-1 ring-maroon/15 opacity-70 hover:opacity-100"
                  }`}
                >
                  <img src={imgSrc(img)} alt={`${product.name} view ${i + 1}`} loading={i === 0 ? "eager" : "lazy"} decoding="async" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </FadeIn>

          <FadeIn delay={0.12}>
            <p className="text-[11px] tracking-[0.4em] uppercase text-gold-dark">{product.category_label}</p>
            <h1 className="mt-3 font-display text-4xl sm:text-5xl text-maroon-deep leading-tight">{product.name}</h1>
            <p data-testid="product-price" className="mt-3 font-display text-2xl sm:text-3xl text-maroon-deep">{inr(product.price)}</p>
            <p className="text-xs text-clay mt-1">GST included · Complimentary shipping across India</p>

            <p className="mt-6 text-sm sm:text-base text-clay leading-relaxed">{product.description}</p>

            <div className="mt-8 sm:mt-10">
              <div className="mb-4">
                <p className="text-[11px] tracking-[0.3em] uppercase text-maroon-deep">Select Size</p>
                <p className="mt-2 text-xs text-clay leading-relaxed">
                  Choose your usual size. Any fit-specific recommendation is noted in the product description above.
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                {product.sizes.map((s) => {
                  const left = product.stock?.[s] ?? 0;
                  const soldOut = left <= 0;
                  return (
                    <button
                      key={s}
                      data-testid={`size-option-${s}`}
                      disabled={soldOut}
                      onClick={() => { setSize(s); setQty(1); }}
                      className={`w-12 h-12 text-sm tracking-wider border transition-all duration-300 ${
                        soldOut
                          ? "border-maroon/10 text-clay/40 line-through cursor-not-allowed"
                          : size === s
                            ? "bg-maroon text-ivory border-maroon"
                            : "border-maroon/25 text-maroon-deep hover:border-gold"
                      }`}
                    >
                      {s}
                    </button>
                  );
                })}
              </div>
              {size && selectedStock > 0 && selectedStock < 5 && (
                <p data-testid="low-stock-note" className="mt-3 text-xs tracking-[0.15em] text-gold-dark uppercase">
                  Only {selectedStock} left in size {size}
                </p>
              )}
            </div>

            <div className="mt-6 sm:mt-8 flex items-stretch gap-3">
              <div className="flex items-center border border-maroon/25">
                <button
                  data-testid="qty-decrease-button"
                  type="button"
                  disabled={!size || qty <= 1}
                  onClick={() => setQty((q) => Math.max(1, q - 1))}
                  className="px-3 sm:px-4 h-full text-maroon-deep hover:bg-ivory-deep transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                  aria-label="Decrease quantity"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <span data-testid="qty-value" className="w-9 sm:w-10 text-center text-maroon-deep">{qty}</span>
                <button
                  data-testid="qty-increase-button"
                  type="button"
                  disabled={!size || qty >= selectedStock}
                  onClick={() => setQty((q) => Math.min(selectedStock, q + 1))}
                  className="px-3 sm:px-4 h-full text-maroon-deep hover:bg-ivory-deep transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                  aria-label="Increase quantity"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              <button
                data-testid="add-to-cart-button"
                type="button"
                disabled={!size || selectedStock === 0}
                onClick={handleAdd}
                className="flex-1 flex items-center justify-center gap-3 bg-maroon text-ivory py-4 text-xs tracking-[0.35em] uppercase hover:bg-maroon-hover transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ShoppingBag className="w-4 h-4" /> Add to Bag
              </button>
            </div>

            <div className="mt-6 grid grid-cols-3 gap-2 border-y border-gold/20 py-4">
              <div className="flex flex-col items-center text-center gap-2 px-1">
                <PackageCheck className="w-4 h-4 text-gold-dark" />
                <p className="text-[9px] leading-relaxed tracking-[0.12em] uppercase text-maroon-deep">Carefully packed</p>
              </div>
              <div className="flex flex-col items-center text-center gap-2 px-1 border-x border-gold/15">
                <ShieldCheck className="w-4 h-4 text-gold-dark" />
                <p className="text-[9px] leading-relaxed tracking-[0.12em] uppercase text-maroon-deep">Secure checkout</p>
              </div>
              <div className="flex flex-col items-center text-center gap-2 px-1">
                <Mail className="w-4 h-4 text-gold-dark" />
                <p className="text-[9px] leading-relaxed tracking-[0.12em] uppercase text-maroon-deep">Email updates</p>
              </div>
            </div>

            <div className="mt-8 sm:mt-10 border-t border-gold/25">
              <Accordion type="single" collapsible>
                <AccordionItem value="fabric" data-testid="accordion-fabric">
                  <AccordionTrigger className="text-xs tracking-[0.3em] uppercase text-maroon-deep">Fabric & Care</AccordionTrigger>
                  <AccordionContent className="text-sm text-clay leading-relaxed">{product.fabric}</AccordionContent>
                </AccordionItem>
                <AccordionItem value="shipping" data-testid="accordion-shipping">
                  <AccordionTrigger className="text-xs tracking-[0.3em] uppercase text-maroon-deep">Shipping</AccordionTrigger>
                  <AccordionContent className="text-sm text-clay leading-relaxed">
                    Dispatched within 2–3 working days, delivered across India in 5–7 days with our compliments. Order updates are sent by email.
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="craft" data-testid="accordion-craft">
                  <AccordionTrigger className="text-xs tracking-[0.3em] uppercase text-maroon-deep">Craftsmanship</AccordionTrigger>
                  <AccordionContent className="text-sm text-clay leading-relaxed">
                    Every Vaiyra piece is cut, printed and finished by hand in small ateliers across India. Slight variations in print and weave are the signature of the human hand — no two pieces are exactly alike.
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>

            <div className="mt-8 flex items-center gap-3 text-gold-dark">
              <LotusMark className="w-5 h-5" />
              <p className="text-[10px] tracking-[0.35em] uppercase">Rooted in Grace</p>
            </div>
          </FadeIn>
        </div>
      </div>

      <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-ivory-card/95 backdrop-blur-xl border-t border-gold/30 px-4 py-3 shadow-[0_-8px_30px_rgba(74,18,26,0.08)]">
        <div className="flex items-center gap-3">
          <div className="min-w-0">
            <p className="text-[9px] tracking-[0.2em] uppercase text-gold-dark">Your selection</p>
            <p className="font-display text-lg text-maroon-deep truncate">{size ? `Size ${size}` : "Choose a size"}</p>
            <p className="text-[10px] text-clay mt-0.5">{inr(product.price * qty)}</p>
          </div>
          <button
            type="button"
            disabled={!size || selectedStock === 0}
            onClick={handleAdd}
            className="ml-auto min-w-[150px] flex items-center justify-center gap-2 bg-maroon text-ivory px-5 py-3.5 text-[10px] tracking-[0.25em] uppercase active:bg-maroon-hover disabled:opacity-45 disabled:cursor-not-allowed"
          >
            <ShoppingBag className="w-4 h-4" /> Add to Bag · {inr(product.price * qty)}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
